import { gql, useApolloClient, useMutation } from "@apollo/client";
import { useCallback } from "react";
import { errorFragment } from "../../../../../gql/GqlErrorFragment";
import {
  DeleteHeadAgreementMutation,
  DeleteHeadAgreementMutationVariables,
} from "@/gql/graphql";

const mutation = gql`
  ${errorFragment}
  mutation DeleteHeadAgreement($headAgreementId: ID!, $rowVersion: String!) {
    productPrice {
      headAgreement {
        deleteHeadAgreement(
          headAgreementId: $headAgreementId
          rowVersion: $rowVersion
        ) {
          ... on ErrorResult {
            ...ErrorFragment
          }
          ... on DeleteHeadAgreementSuccessResult {
            customer {
              id
              preselectedHeadAgreement {
                id
              }
            }
          }
        }
      }
    }
  }
`;
export const useDeleteHeadAgreementMutation = () => {
  const { cache } = useApolloClient();
  const [mutate, { loading, error }] = useMutation<
    DeleteHeadAgreementMutation,
    DeleteHeadAgreementMutationVariables
  >(mutation);

  const deleteHeadAgreement = useCallback(
    (headAgreementId: string, rowVersion: string) =>
      mutate({ variables: { headAgreementId, rowVersion } }),
    [mutate]
  );

  /**
   * Call right before navigating away from the deleted head agreement.
   * Broadcasting the eviction would make the still mounted queries refetch
   * the deleted head agreement, which the backend can no longer find.
   * The next screen reads the cache when it mounts and fetches what is missing.
   */
  const evictDeletedHeadAgreement = useCallback(
    (headAgreementId: string, customerId: string) => {
      cache.evict({
        id: cache.identify({
          __typename: "HeadAgreement",
          id: headAgreementId,
        }),
        broadcast: false,
      });
      cache.evict({
        id: cache.identify({ __typename: "Customer", id: customerId }),
        broadcast: false,
      });
      cache.gc();
    },
    [cache]
  );

  return { deleteHeadAgreement, evictDeletedHeadAgreement, loading, error };
};
